import { useCallback, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Icon, IconName, Typography } from 'components'
import { useAuth } from 'store/auth/hooks'
import { getProfileDetailRoute, ROUTES } from 'settings/constants'
import { useNotification } from 'store/notification/hooks'
import { usePrivy } from '@privy-io/react-auth'
import { track, resetUser } from 'utils/analytics'

import {
  isSideMenuItemVisible,
  lanQueueMenuItem,
  SideMenuItem,
} from '../Layout/sidemenu'

interface BottomNavProps {
  onOpenSettings?: () => void
}

const bottomNavItems: SideMenuItem[] = [
  {
    path: ROUTES.Home,
    label: 'Home',
    iconName: 'explore',
  },
  {
    path: ROUTES.Tournament,
    label: 'Tournaments',
    iconName: 'tournament',
  },
  lanQueueMenuItem,
  {
    path: ROUTES.Notifications,
    label: 'Alerts',
    iconName: 'bell',
    isAuthRequired: true,
  },
  {
    path: ROUTES.Profile,
    label: 'Profile',
    iconName: 'account',
    isAuthRequired: true,
  },
]

const moreMenuItems: SideMenuItem[] = [
  {
    path: ROUTES.Game,
    label: 'Games',
    iconName: 'game',
    isAuthRequired: false,
  },
  {
    path: ROUTES.Team,
    label: 'Teams',
    iconName: 'team',
    isAuthRequired: true,
  },
  {
    path: ROUTES.Community,
    label: 'Community',
    iconName: 'group',
    isAuthRequired: false,
  },
  {
    path: ROUTES.League,
    label: 'Leagues',
    iconName: 'league',
    isAuthRequired: false,
  },
  {
    path: ROUTES.GameServers,
    label: 'Game Servers',
    iconName: 'server',
    isAuthRequired: true,
  },
  {
    path: ROUTES.StreamBrowse,
    label: 'Streams',
    iconName: 'video',
    isAuthRequired: false,
  },
  {
    path: ROUTES.Wallet,
    label: 'Wallet',
    iconName: 'wallet',
    isAuthRequired: true,
  },
  {
    path: ROUTES.Referrals,
    label: 'Referrals',
    iconName: 'gift',
    isAuthRequired: true,
  },
]

export const BottomNav = ({ onOpenSettings }: BottomNavProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isLoggedIn, user, signOut } = useAuth()
  const { unReadNotifications } = useNotification()
  const { logout } = usePrivy()
  const [showMoreMenu, setShowMoreMenu] = useState(false)

  const handleLogout = useCallback(async () => {
    track('auth_logout')
    resetUser()
    setShowMoreMenu(false)
    // Wait for Privy before clearing Redux (see Layout handleLogout).
    try {
      await logout()
    } finally {
      signOut()
      navigate('/')
    }
  }, [logout, signOut, navigate])

  const handleOpenSettings = useCallback(() => {
    setShowMoreMenu(false)
    onOpenSettings?.()
  }, [onOpenSettings])

  const handleNavigate = useCallback(
    (path: string) => {
      if (path === ROUTES.Profile && user && user?.username) {
        navigate(getProfileDetailRoute(user?.username))
      } else {
        navigate(path)
      }
      setShowMoreMenu(false)
    },
    [navigate, user],
  )

  const isActive = (path: string) => {
    if (path === ROUTES.Profile && user?.username) {
      return location.pathname.includes(`/profile/${user.username}`)
    }
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  return (
    <>
      {/* More Menu Drawer */}
      {showMoreMenu && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50"
          onClick={() => setShowMoreMenu(false)}
        >
          <div
            className="fixed bottom-16 left-0 right-0 bg-dark-card border-t border-dark-border max-h-[50vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-3 gap-2 p-4">
              {moreMenuItems
                .filter((item) =>
                  isSideMenuItemVisible(item, { isLoggedIn, user }),
                )
                .map((item) => {
                  const active = isActive(item.path)
                  return (
                    <button
                      key={item.path}
                      aria-label={item.label}
                      onClick={() => handleNavigate(item.path)}
                      className={`flex flex-col items-center justify-center p-4 rounded-lg transition-colors ${
                        active
                          ? 'bg-green bg-opacity-20 text-green'
                          : 'bg-dark-gray-light text-dark-typo-gray hover:text-white'
                      }`}
                    >
                      <Icon name={item.iconName as IconName} size={28} />
                      <Typography
                        className="text-xs mt-2 text-center"
                        fontWeight={active ? 'bold' : 'medium'}
                      >
                        {item.label}
                      </Typography>
                    </button>
                  )
                })}
            </div>

            {/* Settings & Logout Section - Only shown when logged in */}
            {isLoggedIn && (
              <div className="border-t border-dark-border px-4 pb-4 pt-2">
                <div className="flex gap-2">
                  <button
                    onClick={handleOpenSettings}
                    className="flex-1 flex items-center justify-center gap-2 p-3 rounded-lg bg-dark-gray-light text-dark-typo-gray hover:text-white transition-colors"
                  >
                    <Icon name="cog" size={20} />
                    <Typography className="text-sm" fontWeight="medium">
                      Settings
                    </Typography>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex-1 flex items-center justify-center gap-2 p-3 rounded-lg bg-dark-gray-light text-pink hover:text-red-400 transition-colors"
                  >
                    <Icon name="logout" size={20} />
                    <Typography className="text-sm" fontWeight="medium">
                      Logout
                    </Typography>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-dark-card border-t border-dark-border safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {bottomNavItems
            .filter((item) => isSideMenuItemVisible(item, { isLoggedIn, user }))
            .map((item) => {
              const active = isActive(item.path)
              return (
                <button
                  key={item.path}
                  aria-label={item.label}
                  onClick={() => handleNavigate(item.path)}
                  className={`relative flex flex-col items-center justify-center min-w-[44px] xs:min-w-[56px] h-12 px-1 xs:px-2 rounded-lg transition-colors ${
                    active
                      ? 'text-green'
                      : 'text-dark-typo-gray hover:text-white'
                  }`}
                >
                  <div className="relative">
                    <Icon name={item.iconName as IconName} size={24} />
                    {item.path === ROUTES.Notifications && unReadNotifications > 0 && (
                      <div className="absolute -top-1 -right-1 bg-red text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        {unReadNotifications > 9 ? '9+' : unReadNotifications}
                      </div>
                    )}
                  </div>
                  <Typography
                    className="text-[10px] mt-0.5 leading-none hidden xs:block truncate max-w-[52px]"
                    fontWeight={active ? 'bold' : 'medium'}
                  >
                    {item.label}
                  </Typography>
                </button>
              )
            })}

          {/* More Button */}
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className={`relative flex flex-col items-center justify-center min-w-[44px] xs:min-w-[56px] h-12 px-1 xs:px-2 rounded-lg transition-colors ${
              showMoreMenu
                ? 'text-green'
                : 'text-dark-typo-gray hover:text-white'
            }`}
          >
            <Icon name="menu" size={24} />
            <Typography
              className="text-[10px] mt-0.5 leading-none hidden xs:block"
              fontWeight={showMoreMenu ? 'bold' : 'medium'}
            >
              More
            </Typography>
          </button>
        </div>
      </nav>
    </>
  )
}
